import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LinkContainer } from 'react-router-bootstrap'
import { Row, Col, Button, Spinner } from 'react-bootstrap'
import { listTournaments } from '../actions/tournamentActions'
import { listBlogPosts } from '../actions/blogActions'
import { listRssArticles } from '../actions/rssActions'
import { getHomeHero } from '../actions/homeActions'

// ── Hero carousel helpers ──────────────────────────────────────────────────────

function buildSlides(heroData) {
  if (!heroData) return []
  const { tournament, contests, awards } = heroData
  // Tournament logo + name appear top-right on EVERY slide as branding
  const branding = { logoUrl: tournament.logo_url, tournamentName: tournament.name, date: tournament.start_date }
  const slides = []

  if (tournament.winners && tournament.winners.length) {
    slides.push({
      type: 'tournament', tag: 'BNB Champions', title: tournament.name,
      winners: tournament.winners, prizeMoney: null,
      imageUrl: tournament.image_url,
      ...branding,
    })
  }
  contests.forEach(c => {
    if (c.winner) slides.push({
      type: 'contest', tag: '🎯 Contest', title: c.name,
      winners: [c.winner], prizeMoney: c.prize_money || null,
      imageUrl: c.image_url || tournament.image_url,
      resultYards: c.result_yards || null,
      ...branding,
    })
  })
  awards.forEach(a => {
    if (a.winner) slides.push({
      type: 'award', tag: '🎖️ Award', title: a.name,
      winners: [a.winner], prizeMoney: null,
      description: a.description || '',
      imageUrl: a.image_url || tournament.image_url,
      ...branding,
    })
  })
  return slides
}

// WinnerCard — auto-width (sized to image), no overlays, no shadows
function WinnerCard({ winner, index = 0 }) {
  return (
    <div style={{
      position: 'relative', height: '100%', flexShrink: 0,
      marginLeft: index > 0 ? '-38px' : 0,
      zIndex: index,
    }}>
      {winner.image
        ? <img src={winner.image} alt={winner.name}
              className="bnb-homescreen-carousel-player-img"
            style={{  display: 'block' }} />
        : /* Default profile image */
          <img src="/bnb-profile-default.png" alt="Player"
            className="bnb-homescreen-carousel-player-img"
            style={{  display: 'block' }} />
      }
      {/* Name pill */}
      <div style={{
        position: 'absolute', bottom: '0.5rem', left: 0, right: 0,
        display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          background: 'var(--bnb-navy)',
          border: '1.5px solid var(--bnb-gold)',
          borderRadius: 999,
          padding: '0.2rem 0.7rem',
          color: '#fff', fontWeight: 900, fontStyle: 'italic',
          fontSize: 'clamp(0.6rem, 1.4vw, 0.82rem)',
          whiteSpace: 'nowrap', maxWidth: '90%',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {winner.name}
        </div>
      </div>
    </div>
  )
}

function DefaultHero({ userInfo }) {
  return (
    <div className="bnb-hero mb-4">
      <div className="bnb-hero-eyebrow"><i className="fa-solid fa-flag me-2"></i>Official Club Site</div>
      <h1 className="bnb-hero-title">
        {userInfo ? `Welcome back, ${userInfo.name.split(' ')[0]}.` : 'BNB Golf'}
      </h1>
      <p className="bnb-hero-sub">
        {userInfo
          ? 'Track your stats, register for tournaments, and compete for the championship.'
          : 'The official home of BNB Golf Club — tournaments, stats, and more.'}
      </p>
      {!userInfo && (
        <LinkContainer to="/login">
          <Button variant="outline-light" className="mt-3"
            style={{ fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.8rem' }}>
            Sign In
          </Button>
        </LinkContainer>
      )}
    </div>
  )
}

// Per-slide background overlay palette — cycles for contests/awards
const SLIDE_OVERLAYS = [
  ['rgba(0,22,72,0.68)',  'rgba(0,8,32,0.82)'],     // 0: deep navy   (tournament)
  ['rgba(0,64,76,0.68)',  'rgba(0,20,36,0.82)'],     // 1: teal
  ['rgba(52,14,84,0.68)', 'rgba(16,4,34,0.82)'],     // 2: purple
  ['rgba(4,60,36,0.68)',  'rgba(0,18,10,0.82)'],     // 3: forest
  ['rgba(78,34,0,0.68)',  'rgba(30,10,0,0.82)'],     // 4: amber
  ['rgba(62,4,18,0.68)',  'rgba(22,0,6,0.82)'],      // 5: crimson
  ['rgba(18,28,64,0.68)', 'rgba(6,8,30,0.82)'],      // 6: indigo
]

function HeroCarousel({ heroData, userInfo }) {
  const slides = useMemo(() => buildSlides(heroData), [heroData])
  const [current, setCurrent] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => { setCurrent(0) }, [heroData])

  useEffect(() => {
    if (paused || slides.length <= 1) return
    const t = setInterval(() => setCurrent(c => (c + 1) % slides.length), 5500)
    return () => clearInterval(t)
  }, [paused, slides.length])

  if (!slides.length) return <DefaultHero userInfo={userInfo} />

  return (
    <>
    <div
      style={{ borderRadius: 18, overflow: 'hidden', height: 420, position: 'relative', marginBottom: 0, cursor: 'default', userSelect: 'none' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, i) => {
        const [c1, c2] = SLIDE_OVERLAYS[i % SLIDE_OVERLAYS.length]
        const bg = slide.imageUrl
          ? `linear-gradient(135deg, ${c1} 0%, ${c2} 100%), url(${slide.imageUrl}) center/cover no-repeat`
          : `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`
        const isActive = i === current

        return (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            background: bg,
            opacity: isActive ? 1 : 0,
            transform: isActive ? 'scale(1)' : 'scale(1.035)',
            transition: 'opacity 0.75s ease, transform 0.75s ease',
            pointerEvents: isActive ? 'auto' : 'none',
          }}>
            {/* Sparkles */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
              {['✦','✧','✦','✧','✦','✧'].map((s, j) => (
                <span key={j} style={{
                  position: 'absolute',
                  top: `${12 + j * 14}%`,
                  left: `${8 + j * 15}%`,
                  color: 'var(--bnb-gold)',
                  fontSize: j % 2 === 0 ? '0.7rem' : '0.45rem',
                  opacity: 0.55,
                  animation: `heroSparkle ${2.2 + j * 0.35}s ease-in-out infinite`,
                  animationDelay: `${j * 0.4}s`,
                }}>{s}</span>
              ))}
            </div>

            {/* Content layer */}
            <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', padding: '1.5rem 2rem 0' }}>

              {/* ── Top row ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                {/* LEFT: tag + title + date */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.22rem', maxWidth: '55%' }}>
                  <div style={{ color: 'var(--bnb-gold)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.16em', fontWeight: 900, textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
                    {slide.tag}
                  </div>
                  <div style={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.2, textShadow: '0 2px 12px rgba(0,0,0,0.9)' }}>
                    {slide.title}
                  </div>
                  {slide.date && (
                    <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.62rem', textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
                      {new Date(slide.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </div>
                {/* RIGHT: tournament logo + name */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                  {slide.logoUrl
                    ? <img src={slide.logoUrl} alt="" style={{ width: 96, height: 96, objectFit: 'contain', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.75))' }} />
                    : <span style={{ fontSize: '3rem', filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.8))' }}>⛳</span>
                  }
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.72rem', textAlign: 'center', maxWidth: 120, lineHeight: 1.25, textShadow: '0 1px 8px rgba(0,0,0,0.9)' }}>
                    {slide.tournamentName}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Congratulations: tournament only, centered at top row level ── */}
            {slide.type === 'tournament' && <div className={`bnb-tournament-congrats bnb-tournament-congrats-${slide.winners.length}`} style={{
              position: 'absolute', top: '1.5rem', left: 0, right: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
              pointerEvents: 'none', zIndex: 2,
            }}>
              <div style={{
                fontWeight: 900, fontSize: '1.5rem', letterSpacing: '0.18em', textTransform: 'uppercase',
                background: 'linear-gradient(90deg, #c8a951 0%, #fff 30%, #f0d080 55%, #c8a951 80%, #fff 100%)',
                backgroundSize: '250% auto',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                animation: 'shimmerText 2.8s linear infinite',
              }}>
                <span className="mobileHide">🏆</span> &nbsp;Congratulations&nbsp; <span className="mobileHide">🏆</span>
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.8rem',
                letterSpacing: '0.05em', textAlign: 'center',
                textShadow: '0 1px 8px rgba(0,0,0,0.9)',
              }}>
                for winning BNB <span style={{ color: 'var(--bnb-gold)', fontWeight: 900 }}>{slide.title}</span>
              </div>
              {slide.prizeMoney && (
                <div style={{
                  marginTop: '0.25rem',
                  background: 'rgba(200,169,81,0.14)',
                  border: '1px solid rgba(200,169,81,0.45)',
                  borderRadius: 12, padding: '0.3rem 1.2rem',
                  textAlign: 'center',
                  animation: 'confettiBounce 3s ease-in-out infinite',
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.52rem', textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                    Winner takes home
                  </div>
                  <div style={{
                    fontWeight: 900, fontSize: '1.5rem', lineHeight: 1.1,
                    background: 'linear-gradient(90deg, #c8a951, #f5e07a, #c8a951)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    animation: 'shimmerText 2.2s linear infinite',
                  }}>
                    {slide.prizeMoney}
                  </div>
                </div>
              )}
            </div>}

            {/* ── Body: type-specific layout ── */}

            {slide.type === 'tournament' && (
              <div className={`bnb-tournament-winners bnb-tournament-winners-${slide.winners.length}`} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', height: 315, justifyContent: 'center', gap: 0, overflow: 'hidden' }}>
                {slide.winners.map((w, j) => <WinnerCard key={j} winner={w} index={j} />)}
              </div>
            )}

            {slide.type === 'contest' && (
              <>
                {/* LEFT: congratulations + prize money */}
                <div className="bnb-contest-text" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '2rem', width: '42%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ fontWeight: 900, fontSize: '1.1rem', letterSpacing: '0.18em', textTransform: 'uppercase', background: 'linear-gradient(90deg, #c8a951 0%, #fff 30%, #f0d080 55%, #c8a951 80%, #fff 100%)', backgroundSize: '250% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmerText 2.8s linear infinite', textAlign: 'center' }}>
                    <span className="mobileHide">🏆 &nbsp;</span>
                    Congratulations
                    <span className="mobileHide">&nbsp; 🏆</span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.05em', textAlign: 'center', textShadow: '0 1px 8px rgba(0,0,0,0.9)', marginBottom: '0.2rem' }}>
                    <span className="MVshow">For winning </span><span style={{ color: 'var(--bnb-gold)', fontWeight: 900 }}>{slide.title}</span>
                  </div>
                  {slide.resultYards && (
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em', textAlign: 'center', textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
                      {slide.resultYards} Distance
                    </div>
                  )}
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.52rem', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, marginTop: '0.3rem' }}>Contest Prize</div>
                  <div style={{ fontWeight: 900, fontSize: '2.8rem', lineHeight: 1.1, background: 'linear-gradient(90deg, #c8a951, #f5e07a, #c8a951)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmerText 2.2s linear infinite' }}>
                    ${slide.prizeMoney || '—'}
                  </div>
                </div>
                {/* RIGHT: player image */}
                <div className="bnb-contest-player" style={{ position: 'absolute', bottom: 0, right: '22%', height: 300, pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ position: 'relative', height: '100%' }}>
                    <img src={slide.winners[0]?.image || '/bnb-profile-default.png'} alt={slide.winners[0]?.name} style={{ height: '100%', width: 'auto', display: 'block' }} />
                    <div style={{ position: 'absolute', bottom: '0.5rem', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <div style={{ background: 'var(--bnb-navy)', border: '1.5px solid var(--bnb-gold)', borderRadius: 999, padding: '0.2rem 0.7rem', color: '#fff', fontWeight: 900, fontStyle: 'italic', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {slide.winners[0]?.name}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {slide.type === 'award' && (
              <>
                {/* LEFT: player image — moved toward center */}
                <div className="bnb-award-player" style={{ position: 'absolute', bottom: 0, left: '22%', height: 300, pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ position: 'relative', height: '100%' }}>
                    <img src={slide.winners[0]?.image || '/bnb-profile-default.png'} alt={slide.winners[0]?.name} style={{ height: '100%', width: 'auto', display: 'block' }} />
                    <div style={{ position: 'absolute', bottom: '0.5rem', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <div style={{ background: 'var(--bnb-navy)', border: '1.5px solid var(--bnb-gold)', borderRadius: 999, padding: '0.2rem 0.7rem', color: '#fff', fontWeight: 900, fontStyle: 'italic', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {slide.winners[0]?.name}
                      </div>
                    </div>
                  </div>
                </div>
                {/* RIGHT: congratulations + award description */}
                <div className="bnb-award-text" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '2rem', width: '42%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ fontWeight: 900, fontSize: '1.1rem', letterSpacing: '0.18em', textTransform: 'uppercase', textAlign: 'center', background: 'linear-gradient(90deg, #c8a951 0%, #fff 30%, #f0d080 55%, #c8a951 80%, #fff 100%)', backgroundSize: '250% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmerText 2.8s linear infinite' }}>
                    <span className="bnb-award-emoji-left">🏆 &nbsp;</span>
                    Congratulations
                    <span className="bnb-award-emoji-right">&nbsp; 🏆</span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.05em', textShadow: '0 1px 8px rgba(0,0,0,0.9)', marginBottom: '0.2rem', textAlign: 'center' }}>
                    <span className="MVshow">For winning </span><span style={{ color: 'var(--bnb-gold)', fontWeight: 900 }}>{slide.title}</span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.88rem', lineHeight: 1.55, fontWeight: 600, textShadow: '0 1px 8px rgba(0,0,0,0.9)', textAlign: 'center' }}>
                    {slide.description || slide.title}
                  </div>
                </div>
              </>
            )}
          </div>
        )
      })}

    </div>

    {/* ── Dot nav: below the card ── */}
    {slides.length > 1 && (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.45rem', marginTop: '0.6rem', marginBottom: '0.5rem' }}>
        {slides.map((_, k) => (
          <button
            key={k}
            onClick={() => setCurrent(k)}
            style={{
              width: k === current ? 22 : 7, height: 7, borderRadius: 4,
              background: k === current ? 'var(--bnb-gold)' : 'rgba(0,30,87,0.25)',
              border: k === current ? 'none' : '1px solid rgba(0,30,87,0.3)',
              cursor: 'pointer', padding: 0,
              transition: 'all 0.35s ease',
              boxShadow: k === current ? '0 0 8px rgba(200,169,81,0.5)' : 'none',
            }}
          />
        ))}
      </div>
    )}
    </>
  )
}

function HomeScreen() {
  const dispatch = useDispatch()

  const userLogin = useSelector(state => state.userLogin)
  const { userInfo } = userLogin

  const tournamentList = useSelector(state => state.tournamentList)
  const { loading: tLoading, tournaments } = tournamentList

  const { data: heroData } = useSelector(state => state.homeHero)

  const blogList = useSelector(state => state.blogList)
  const { loading: bLoading, posts = [] } = blogList

  const { articles: rssArticles = [] } = useSelector(state => state.rssArticles)

  const [activeFilter, setActiveFilter] = useState(null)
  const [selectedPost, setSelectedPost] = useState(null)

  const categories = posts.length
    ? [...new Map(posts.map(p => [p.category, p.category_display])).entries()]
        .map(([value, label]) => ({ value, label }))
    : []
  const filteredPosts = activeFilter ? posts.filter(p => p.category === activeFilter) : posts

  // When no filter: mix blog posts + RSS articles sorted by date.
  // 'rss' filter: only RSS articles. Category filter: only matching blog posts.
  const combinedItems = activeFilter === 'rss'
    ? rssArticles
    : activeFilter
      ? filteredPosts
      : [...filteredPosts, ...rssArticles].sort((a, b) => {
          const dA = new Date(a.created_at || a.published || 0)
          const dB = new Date(b.created_at || b.published || 0)
          return dB - dA
        })

  useEffect(() => {
    dispatch(listTournaments())
    dispatch(listBlogPosts())
    dispatch(listRssArticles())
    dispatch(getHomeHero())
  }, [dispatch])

  // Sort tournaments — most recent first (by start_date if available, otherwise id)
  const sortedTournaments = tournaments
    ? [...tournaments].sort((a, b) => {
        if (a.start_date && b.start_date) return new Date(b.start_date) - new Date(a.start_date)
        return b.id - a.id
      })
    : []

  // Active + upcoming tournaments sorted nearest-first
  const upcomingTournaments = sortedTournaments
    .filter(t => t.status === 'upcoming' || t.status === 'active')
    .sort((a, b) => {
      if (a.start_date && b.start_date) return new Date(a.start_date) - new Date(b.start_date)
      return a.id - b.id
    })

  // Most recent completed tournament that has a winner
  const lastChampion = sortedTournaments.find(t => t.status === 'completed' && t.winner_names && t.winner_names.length > 0)

  // Winners for the recent champion card — prefer heroData (has images), else name-only fallback
  const champWinners = heroData?.tournament?.winners?.length
    ? heroData.tournament.winners
    : (lastChampion?.winner_names || []).map(name => ({ name, image: null }))

  // Top 5 players by BNB tournament wins (tournaments only, no contests/awards)
  const tournamentWins = (() => {
    const map = {}
    sortedTournaments
      .filter(t => t.status === 'completed' && t.winner_names?.length > 0)
      .forEach(t => { t.winner_names.forEach(name => { map[name] = (map[name] || 0) + 1 }) })
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, wins], idx) => ({ name, wins, rank: idx + 1 }))
  })()

  return (
    <div style={{ paddingTop: '0.5rem' }}>

      {/* ── Hero Carousel ─────────────────────────────────────── */}
      <HeroCarousel heroData={heroData} userInfo={userInfo} />

      {/* ── Main two-column layout ─────────────────────────────── */}
      <Row className="mt-2">

        {/* ── Left: News / Latest ───────────────────────────────── */}
        <Col lg={8} className="mb-4">

          {/* Section label + filter chips */}
          <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.75rem', borderBottom: '2px solid var(--bnb-navy)', paddingBottom: '0.4rem' }}>
            <div
              className="bnb-section-label"
              onClick={() => setActiveFilter(null)}
              style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0, cursor: 'pointer', opacity: activeFilter === null ? 1 : 0.45 }}
            >Latest</div>
            {categories.map(cat => (
              <span
                key={cat.value}
                onClick={() => setActiveFilter(activeFilter === cat.value ? null : cat.value)}
                style={{
                  fontSize: '0.7rem',
                  fontWeight: activeFilter === cat.value ? '700' : '400',
                  color: activeFilter === cat.value ? 'var(--bnb-navy)' : 'var(--bnb-gray)',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: activeFilter === cat.value ? '2px solid var(--bnb-gold)' : '2px solid transparent',
                  paddingBottom: '1px',
                  userSelect: 'none',
                }}
              >
                {cat.label === 'General' ? 'LOCAL' : cat.label}
              </span>
            ))}
            {/* RSS filter chip */}
            {rssArticles.length > 0 && (
              <span
                onClick={() => setActiveFilter(activeFilter === 'rss' ? null : 'rss')}
                title="RSS Feed"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  fontSize: '0.7rem',
                  fontWeight: activeFilter === 'rss' ? '700' : '400',
                  color: activeFilter === 'rss' ? '#e05b00' : 'var(--bnb-gray)',
                  cursor: 'pointer',
                  borderBottom: activeFilter === 'rss' ? '2px solid #e05b00' : '2px solid transparent',
                  paddingBottom: '1px',
                  userSelect: 'none',
                }}
              >
                <i className="fa-solid fa-rss" style={{ fontSize: '0.75rem', color: activeFilter === 'rss' ? '#e05b00' : '#f26522' }}></i>
                RSS
              </span>
            )}
          </div>

          {/* Scrollable post list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '370px', overflowY: 'auto', paddingRight: '4px' }}>
            {bLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" style={{ color: 'var(--bnb-navy)' }} />
              </div>
            ) : combinedItems.length === 0 ? (
              <div style={{ fontSize: '0.875rem', color: 'var(--bnb-gray)', padding: '1rem 0' }}>
                <i className="fa-solid fa-newspaper me-2"></i>No posts yet. Check back soon.
              </div>
            ) : (
              combinedItems.map(item => item.is_rss ? (
                /* ── RSS external article card ── */
                <a
                  key={item.id}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bnb-news-card"
                  style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="bnb-news-thumb">
                    {item.image_url
                      ? <img src={item.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
                      : <i className="fa-solid fa-rss" style={{ fontSize: '1.5rem', color: 'var(--bnb-gray)' }}></i>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="bnb-news-category" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ background: '#e8f0fe', color: '#1a56db', borderRadius: '3px', padding: '0 5px', fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {item.source_name}
                      </span>
                      <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '0.6rem', color: 'var(--bnb-gray)' }}></i>
                    </div>
                    <div className="bnb-news-headline">{item.title}</div>
                    <div className="bnb-news-meta">
                      {item.published ? new Date(item.published).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </div>
                  </div>
                </a>
              ) : (
                /* ── Internal blog post card ── */
                <div
                  key={item.id}
                  className="bnb-news-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedPost(item)}
                >
                  <div className="bnb-news-thumb">
                    {item.image_desktop_url
                      ? <img src={item.image_desktop_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
                      : <i className="fa-solid fa-golf-ball-tee" style={{ fontSize: '1.5rem' }}></i>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="bnb-news-category">{item.category_display}</div>
                    <div className="bnb-news-headline">{item.title}</div>
                    <div className="bnb-news-meta">
                      {item.author_name} &middot; {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Col>

        {/* ── Right: Tournament sidebar widget ─────────────────── */}
        <Col lg={4} className="mb-4">
          <div className="bnb-section-label">Events</div>

          <div className="bnb-tournament-card">
            <div className="bnb-tournament-card-header">
              <span className="bnb-official-badge me-2">Official</span>
              Tournament Schedule
            </div>

            {tLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" style={{ color: 'var(--bnb-navy)' }} />
              </div>
            ) : (
              <>
                {/* ── Active / Upcoming ── */}
                {upcomingTournaments.length === 0 ? (
                  <div style={{ padding: '1rem 1rem 0.5rem', fontSize: '0.8rem', color: 'var(--bnb-gray)', textAlign: 'center' }}>
                    <i className="fa-solid fa-calendar-xmark mb-1" style={{ fontSize: '1.2rem', display: 'block', color: '#d1d5db' }}></i>
                    No upcoming events right now.
                  </div>
                ) : (
                  upcomingTournaments.map((t, idx) => (
                    <LinkContainer to={`/events`} key={t.id}>
                      <div className="bnb-tournament-row">
                        <div className="bnb-pos-badge">{idx + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '700', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            {t.name}
                            {t.status === 'active' && (
                              <span style={{
                                background: '#dc2626', color: '#fff',
                                fontSize: '0.52rem', fontWeight: 900,
                                padding: '0.1rem 0.38rem', borderRadius: 3,
                                letterSpacing: '0.1em',
                                animation: 'livePulse 1.5s ease-in-out infinite',
                                flexShrink: 0,
                              }}>● LIVE</span>
                            )}
                          </div>
                          {t.start_date && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--bnb-gray)' }}>
                              {new Date(t.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: '0.65rem', color: 'var(--bnb-gray)' }}></i>
                      </div>
                    </LinkContainer>
                  ))
                )}

              </>
            )}

            <div style={{ padding: '0.6rem 1rem', borderTop: '1px solid var(--bnb-border)', textAlign: 'right' }}>
              <LinkContainer to="/events" style={{ cursor: 'pointer' }}>
                <span className="bnb-schedule-link" style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--bnb-navy)', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', textDecoration: 'none', display: 'inline-block' }}>
                  Full Schedule <i className="fa-solid fa-arrow-right ms-1"></i>
                </span>
              </LinkContainer>
            </div>
          </div>

          {/* ── Recent Champions ─────────────────────────────── */}
          {champWinners.length > 0 && (
            <div className="mt-3">
              {/* Label row: "Recent Champions" left + tournament name/date right */}
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div className="bnb-section-label" style={{ marginBottom: 0 }}>Recent Champions</div>
                <LinkContainer to={`/events/${lastChampion?.id}`} style={{ cursor: 'pointer' }}>
                <div style={{ textAlign: 'right', lineHeight: 1.3, cursor: 'pointer' }}>
                  <div className="bnb-link-hover" style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--bnb-navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160, textDecoration: 'underline', textDecorationColor: 'var(--bnb-gold)' }}>
                    {heroData?.tournament?.name || lastChampion?.name}
                  </div>
                  {(heroData?.tournament?.date || lastChampion?.start_date) && (
                    <div style={{ fontSize: '0.62rem', color: 'var(--bnb-gray)' }}>
                      {new Date(((heroData?.tournament?.date || lastChampion?.start_date) + 'T00:00:00')).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </div>
                  )}
                </div>
                </LinkContainer>
              </div>
              {/* Profile images — white-to-blue gradient bg, touch the bottom */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', overflow: 'hidden', borderRadius: 10, background: 'linear-gradient(to bottom, #ffffff 0%, var(--bnb-navy) 100%)' }}>
                {champWinners.map((w, j) => (
                  <div key={j} style={{ position: 'relative', flexShrink: 0, marginLeft: j > 0 ? '-14px' : 0, zIndex: champWinners.length - j }}>
                    <img
                      src={w.image || '/bnb-profile-default.png'}
                      alt={w.name}
                      className="bnb-homescreen-champion-img"
                      style={{  display: 'block', filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.25))' }}
                    />
                    <div style={{ position: 'absolute', bottom: '0.3rem', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <div style={{
                        background: 'var(--bnb-navy)',
                        border: '1.5px solid var(--bnb-gold)',
                        borderRadius: 999,
                        padding: '0.08rem 0.45rem',
                        color: '#fff', fontWeight: 900, fontStyle: 'italic', fontSize: '0.58rem',
                        whiteSpace: 'nowrap', maxWidth: '95%', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {w.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* ── Top 5 BNB Tournament Wins ─────────────────────── */}
          <div className="mt-3">
            <div className="bnb-section-label">BNB Rankings</div>
            <div className="bnb-tournament-card">
              <div className="bnb-tournament-card-header">
                <span className="bnb-official-badge me-2">Official</span>
                Tournament Wins
              </div>
              {tLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" style={{ color: 'var(--bnb-navy)' }} />
                </div>
              ) : tournamentWins.length === 0 ? (
                <div style={{ padding: '1rem', fontSize: '0.8rem', color: 'var(--bnb-gray)', textAlign: 'center' }}>
                  <i className="fa-solid fa-trophy mb-1" style={{ fontSize: '1.2rem', display: 'block', color: '#d1d5db' }}></i>
                  No tournament results yet.
                </div>
              ) : (
                tournamentWins.map((entry) => (
                  <LinkContainer to="/leaderboard" key={entry.rank}>
                  <div className="bnb-tournament-row" style={{ cursor: 'pointer' }}>
                    <div className="bnb-pos-badge">{entry.rank}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '700', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {entry.name}
                      </div>
                    </div>
                    <div style={{
                      background: 'var(--bnb-gold)', color: '#fff',
                      borderRadius: 6, padding: '0.15rem 0.55rem',
                      fontSize: '0.75rem', fontWeight: 800, flexShrink: 0,
                    }}>
                      🏆 {entry.wins}
                    </div>
                  </div>
                  </LinkContainer>
                ))
              )}
              <div style={{ padding: '0.6rem 1rem', borderTop: '1px solid var(--bnb-border)', textAlign: 'right' }}>
                <LinkContainer to="/leaderboard">
                  <span className="bnb-schedule-link" style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--bnb-navy)', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Full Leaderboard <i className="fa-solid fa-arrow-right ms-1"></i>
                  </span>
                </LinkContainer>
              </div>
            </div>
          </div>
        </Col>

      </Row>

      {/* ── Blog detail modal ─────────────────────────────────── */}
      {selectedPost && (
        <div
          onClick={() => setSelectedPost(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.78)',
            zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '2rem 1rem',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bnb-hs-blog-container"
            style={{
              width: '70vw',
              maxHeight: '88vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: '10px',
              position: 'relative',
              boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedPost(null)}
              style={{
                position: 'absolute', top: '12px', right: '12px', zIndex: 10,
                background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%',
                width: '42px', height: '42px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: '1.2rem',
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>

            {/* Hero image with title overlay */}
            {selectedPost.image_desktop_url ? (
              <div style={{ position: 'relative', width: '100%' }}>
                <img
                  src={selectedPost.image_desktop_url}
                  alt=""
                  style={{ width: '100%', maxHeight: '380px', objectFit: 'cover', display: 'block', borderRadius: '10px 10px 0 0' }}
                />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '2rem 1.75rem 1.25rem',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)',
                  borderRadius: '0',
                }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: '700', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.4rem' }}>
                    {selectedPost.category_display}
                  </div>
                  <h2 style={{ color: '#fff', fontWeight: '800', fontSize: '1.6rem', margin: 0, textShadow: '0 2px 10px rgba(0,0,0,0.65)' }}>
                    {selectedPost.title}
                  </h2>
                </div>
              </div>
            ) : (
              <div style={{ padding: '2rem 1.75rem 0.5rem' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: '700', color: 'var(--bnb-gray)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.4rem' }}>
                  {selectedPost.category_display}
                </div>
                <h2 style={{ fontWeight: '800', fontSize: '1.6rem', color: 'var(--bnb-navy)', margin: 0 }}>
                  {selectedPost.title}
                </h2>
              </div>
            )}

            {/* Article body */}
            <div style={{ padding: '1.25rem 1.75rem 2.5rem' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--bnb-gray)', marginBottom: '1.1rem', borderBottom: '1px solid var(--bnb-border)', paddingBottom: '0.75rem' }}>
                {selectedPost.author_name} &middot; {new Date(selectedPost.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              {selectedPost.excerpt && (
                <p style={{ fontWeight: '600', fontSize: '0.975rem', color: 'var(--bnb-navy)', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                  {selectedPost.excerpt}
                </p>
              )}
              <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: '#374151', whiteSpace: 'pre-wrap' }}>
                {selectedPost.content}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default HomeScreen
